import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Add as AddIcon,
  Sync as SyncIcon,
  CloudDownload as CloudDownloadIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import api from '../services/api';
import { SafeHtml } from '../utils/safeHtml';

interface Plan {
  _id?: string;
  name: string;
  description: string;
  monthlyFee: number;
  annualFee: number;
  billingPeriod: 'monthly' | 'annual' | 'both' | 'hybrid';
  seatsBased: boolean;
  seatLimit: number | null;
  seatPrice: number;
  agentWide: boolean;
  features: string[];
  isActive: boolean;
  sortOrder: number;
  trialDays?: number;
  storageLimit?: number | null; // GB, null = unlimited
  limits?: string; // JSON string for editing
  stripeProductId?: string;
  stripeMonthlyPriceId?: string;
  stripeAnnualPriceId?: string;
  stripeSeatPriceId?: string;
  // Stripe-specific fields
  statementDescriptor?: string;
  unitLabel?: string;
  taxCode?: string;
  images?: string[];
  metadata?: Record<string, string>;
}

interface StripeProduct {
  id: string;
  name: string;
  description: string;
  active: boolean;
  metadata: any;
  prices: Array<{
    id: string;
    unit_amount: number;
    currency: string;
    recurring: any;
    active: boolean;
  }>;
}

const defaultPlan: Plan = {
  name: '',
  description: '',
  monthlyFee: 0,
  annualFee: 0,
  billingPeriod: 'monthly',
  seatsBased: false,
  seatLimit: null,
  seatPrice: 0,
  agentWide: false,
  features: [],
  isActive: true,
  sortOrder: 0,
  trialDays: 0,
  storageLimit: null,
  limits: '',
  statementDescriptor: '',
  unitLabel: '',
  taxCode: '',
  images: [],
  metadata: {},
};

const AdminPlans = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentPlan, setCurrentPlan] = useState<Plan>(defaultPlan);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [stripeProducts, setStripeProducts] = useState<StripeProduct[]>([]);
  const [syncingFromStripe, setSyncingFromStripe] = useState(false);
  const [showStripeProducts, setShowStripeProducts] = useState(false);
  const [stripeDetails, setStripeDetails] = useState<any>(null);
  const [showStripeDetailsDialog, setShowStripeDetailsDialog] = useState(false);
  const [metadataText, setMetadataText] = useState('');
  const selectAllOnFocus = (e: React.FocusEvent<HTMLInputElement>) => e.target.select();
  const [monthlyFeeText, setMonthlyFeeText] = useState('0');
  const [annualFeeText, setAnnualFeeText] = useState('0');
  const [seatPriceText, setSeatPriceText] = useState('0');
  const [sortOrderText, setSortOrderText] = useState('0');

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/admin/plans');
      setPlans(res.data);
    } catch (err) {
      setError('Failed to fetch plans');
    } finally {
      setLoading(false);
    }
  };

  const fetchStripeProducts = async () => {
    try {
      const res = await api.get('/admin/stripe/products');
      setStripeProducts(res.data);
    } catch (err) {
      setError('Failed to fetch Stripe products');
    }
  };

  const syncFromStripe = async () => {
    setSyncingFromStripe(true);
    setError(null);
    try {
      const res = await api.post('/admin/plans/sync-from-stripe');
      setSuccess(res.data.message);
      fetchPlans(); // Refresh plans after sync
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to sync from Stripe');
    } finally {
      setSyncingFromStripe(false);
    }
  };

  const viewStripeDetails = async (planId: string) => {
    try {
      const res = await api.get(`/admin/plans/${planId}/stripe`);
      setStripeDetails(res.data);
      setShowStripeDetailsDialog(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch Stripe details');
    }
  };

  const handleOpenDialog = (plan?: Plan) => {
    if (plan) {
      setCurrentPlan({
        ...plan,
        limits: plan.limits ? JSON.stringify(plan.limits, null, 2) : '',
      });
      setIsEditing(true);
      setMetadataText(plan.metadata ? JSON.stringify(plan.metadata, null, 2) : '');
      setMonthlyFeeText(String(plan.monthlyFee ?? 0));
      setAnnualFeeText(String(plan.annualFee ?? 0));
      setSeatPriceText(String(plan.seatPrice ?? 0));
      setSortOrderText(String(plan.sortOrder ?? 0));
    } else {
      setCurrentPlan(defaultPlan);
      setIsEditing(false);
      setMetadataText('');
      setMonthlyFeeText('0');
      setAnnualFeeText('0');
      setSeatPriceText('0');
      setSortOrderText('0');
    }
    setOpenDialog(true);
    setSuccess(null);
    setError(null);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentPlan(defaultPlan);
    setIsEditing(false);
    setMetadataText('');
    setSortOrderText('0');
  };

  const handleChange = (field: keyof Plan) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { value: unknown } }
  ) => {
    let value: any = event.target.value;
    if ([
      'monthlyFee',
      'annualFee',
      'sortOrder',
      'trialDays',
    ].includes(field)) {
      value = value === '' ? 0 : Number(value);
      if (isNaN(value)) value = 0;
    }
    // Fields that allow null when empty
    if ([
      'seatLimit',
      'storageLimit',
    ].includes(field)) {
      value = value === '' ? null : Number(value);
      if (value !== null && isNaN(value)) value = null;
    }
    setCurrentPlan(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Leading-zero safe handlers for currency-like fields
  const handleMoneyChange = (field: 'monthlyFee' | 'annualFee' | 'seatPrice') => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const raw = event.target.value;
    // Allow empty while typing
    if (raw === '') {
      if (field === 'monthlyFee') setMonthlyFeeText('');
      if (field === 'annualFee') setAnnualFeeText('');
      if (field === 'seatPrice') setSeatPriceText('');
      setCurrentPlan(prev => ({ ...prev, [field]: 0 }));
      return;
    }
    // Keep digits and a single decimal point
    let cleaned = raw.replace(/[^0-9.]/g, '');
    // Keep only the first dot
    cleaned = cleaned.replace(/(\..*)\./g, '$1');
    // Normalize leading zeros (allow 0.xxx)
    if (cleaned.startsWith('0') && !cleaned.startsWith('0.')) {
      cleaned = cleaned.replace(/^0+/, '');
      if (cleaned === '') cleaned = '0';
    }
    const normalized = cleaned;
    if (field === 'monthlyFee') setMonthlyFeeText(normalized);
    if (field === 'annualFee') setAnnualFeeText(normalized);
    if (field === 'seatPrice') setSeatPriceText(normalized);
    const asNum = parseFloat(normalized || '0');
    setCurrentPlan(prev => ({ ...prev, [field]: isNaN(asNum) ? 0 : asNum }));
  };

  const handleIntegerChange = (field: 'sortOrder') => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const raw = event.target.value;
    if (raw === '') {
      if (field === 'sortOrder') setSortOrderText('');
      setCurrentPlan(prev => ({ ...prev, [field]: 0 }));
      return;
    }
    let cleaned = raw.replace(/[^0-9]/g, '');
    cleaned = cleaned.replace(/^0+(\d)/, '$1');
    if (field === 'sortOrder') setSortOrderText(cleaned);
    const asNum = Number(cleaned || '0');
    setCurrentPlan(prev => ({ ...prev, [field]: isNaN(asNum) ? 0 : asNum }));
  };

  const handleSwitchChange = (field: keyof Plan) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCurrentPlan(prev => ({
      ...prev,
      [field]: event.target.checked,
    }));
  };

  const handleFeaturesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const features = value.split(',').map(f => f.trim()).filter(Boolean);
    setCurrentPlan(prev => ({ ...prev, features }));
  };

  const handleMetadataChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMetadataText(event.target.value);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const url = isEditing
        ? `/api/admin/plans/${currentPlan._id}`
        : '/api/admin/plans';

      let limitsObj = undefined;
      if (currentPlan.limits) {
        try {
          limitsObj = JSON.parse(currentPlan.limits);
        } catch {
          setError('Limits must be valid JSON');
          setSaving(false);
          return;
        }
      }

      let metadataObj = undefined;
      if (metadataText && metadataText.trim().length > 0) {
        try {
          metadataObj = JSON.parse(metadataText);
        } catch {
          setError('Metadata must be valid JSON');
          setSaving(false);
          return;
        }
      }

      const planToSend = {
        ...currentPlan,
        limits: limitsObj,
        metadata: metadataObj,
      };

      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(planToSend),
      });

      if (!response.ok) {
        throw new Error('Failed to save plan');
      }

      setSuccess(`Plan ${isEditing ? 'updated' : 'created'} successfully`);
      handleCloseDialog();
      fetchPlans();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (planId: string) => {
    if (!window.confirm('Are you sure you want to archive this plan?')) {
      return;
    }

    try {
      const response = await fetch(`/api/admin/plans/${planId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        // Handle constraint: plan with active subscriptions cannot be deleted
        if (response.status === 409) {
          const data = await response.json().catch(() => ({}));
          const message = data?.message || 'Cannot delete plan with active subscriptions';
          setError(message);
          return;
        }
        throw new Error('Failed to archive plan');
      }

      setSuccess('Plan archived successfully');
      fetchPlans();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to archive plan');
    }
  };

  if (loading) {
    return (
      <Container>
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography>Loading plans...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Subscription Plans
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<CloudDownloadIcon />}
              onClick={fetchStripeProducts}
            >
              View Stripe Products
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<SyncIcon />}
              onClick={syncFromStripe}
              disabled={syncingFromStripe}
            >
              {syncingFromStripe ? 'Syncing...' : 'Sync from Stripe'}
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add Plan
            </Button>
          </Box>
        </Box>

        {/* Stripe Products Section */}
        {stripeProducts.length > 0 && (
          <Paper sx={{ mb: 3, p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Stripe Products ({stripeProducts.length})
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Product Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Prices</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stripeProducts.map((product) => (
                    <TableRow key={product.id}>
                      <TableCell>{product.name}</TableCell>
                      <TableCell>
                        <SafeHtml 
                          content={product.description}
                          style={{ 
                            maxWidth: '200px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical'
                          }}
                        />
                      </TableCell>
                      <TableCell>{product.active ? 'Active' : 'Inactive'}</TableCell>
                      <TableCell>
                        {product.prices.map((price) => (
                          <div key={price.id}>
                            ${price.unit_amount} {price.currency.toUpperCase()} / {price.recurring?.interval}
                          </div>
                        ))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        <TableContainer component={Paper} sx={{ position: 'relative' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Monthly Fee</TableCell>
                <TableCell align="right">Annual Fee</TableCell>
                <TableCell>Billing Period</TableCell>
                <TableCell>Stripe Status</TableCell>
                <TableCell>Seats Based</TableCell>
                <TableCell>Seat Limit</TableCell>
                <TableCell>Agent Wide</TableCell>
                <TableCell>Features</TableCell>
                <TableCell>Trial Days</TableCell>
                <TableCell>Sort Order</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right" sx={{ position: 'sticky', right: 0, backgroundColor: '#fff', zIndex: 2 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plans.map((plan) => (
                <TableRow key={plan._id}>
                  <TableCell>{plan.name}</TableCell>
                  <TableCell>
                    <SafeHtml 
                      content={plan.description}
                      style={{ 
                        maxWidth: '300px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical'
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">${plan.monthlyFee}</TableCell>
                  <TableCell align="right">${plan.annualFee}</TableCell>
                  <TableCell>{plan.billingPeriod}</TableCell>
                  <TableCell>
                    {plan.stripeProductId ? (
                      <span style={{ color: 'green' }}>✓ Synced</span>
                    ) : (
                      <span style={{ color: 'orange' }}>Not Synced</span>
                    )}
                  </TableCell>
                  <TableCell>{plan.seatsBased ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{plan.seatLimit ?? '—'}</TableCell>
                  <TableCell>{plan.agentWide ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{plan.features.join(', ')}</TableCell>
                  <TableCell>{plan.trialDays ?? 0}</TableCell>
                  <TableCell>{plan.sortOrder}</TableCell>
                  <TableCell>{plan.isActive ? 'Active' : 'Inactive'}</TableCell>
                  <TableCell align="right" sx={{ position: 'sticky', right: 0, backgroundColor: '#fff' }}>
                    <IconButton 
                      onClick={() => plan._id && viewStripeDetails(plan._id)}
                      title="View Stripe Details"
                    >
                      <InfoIcon />
                    </IconButton>
                    <IconButton onClick={() => handleOpenDialog(plan)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => plan._id && handleDelete(plan._id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Stripe Details Dialog */}
        <Dialog open={showStripeDetailsDialog} onClose={() => setShowStripeDetailsDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Stripe Details</DialogTitle>
          <DialogContent>
            {stripeDetails && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Plan: {stripeDetails.name}
                </Typography>
                
                {stripeDetails.stripeProduct && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Stripe Product
                    </Typography>
                    <Box sx={{ pl: 2 }}>
                      <Typography><strong>ID:</strong> {stripeDetails.stripeProduct.id}</Typography>
                      <Typography><strong>Name:</strong> {stripeDetails.stripeProduct.name}</Typography>
                      <Typography><strong>Description:</strong></Typography>
                      <Box sx={{ pl: 1, mb: 1 }}>
                        <SafeHtml content={stripeDetails.stripeProduct.description} />
                      </Box>
                      <Typography><strong>Active:</strong> {stripeDetails.stripeProduct.active ? 'Yes' : 'No'}</Typography>
                      {stripeDetails.stripeProduct.statement_descriptor && (
                        <Typography><strong>Statement Descriptor:</strong> {stripeDetails.stripeProduct.statement_descriptor}</Typography>
                      )}
                      {stripeDetails.stripeProduct.unit_label && (
                        <Typography><strong>Unit Label:</strong> {stripeDetails.stripeProduct.unit_label}</Typography>
                      )}
                      {stripeDetails.stripeProduct.tax_code && (
                        <Typography><strong>Tax Code:</strong> {stripeDetails.stripeProduct.tax_code}</Typography>
                      )}
                      {stripeDetails.stripeProduct.images && stripeDetails.stripeProduct.images.length > 0 && (
                        <Typography><strong>Images:</strong> {stripeDetails.stripeProduct.images.join(', ')}</Typography>
                      )}
                      {stripeDetails.stripeProduct.metadata && Object.keys(stripeDetails.stripeProduct.metadata).length > 0 && (
                        <Box>
                          <Typography><strong>Metadata:</strong></Typography>
                          <Box sx={{ pl: 2 }}>
                            {Object.entries(stripeDetails.stripeProduct.metadata).map(([key, value]) => (
                              <Typography key={key} variant="body2">
                                {key}: {String(value)}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
                
                {stripeDetails.stripePrices && stripeDetails.stripePrices.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Stripe Prices
                    </Typography>
                    {stripeDetails.stripePrices.map((price: any, index: number) => (
                      <Box key={index} sx={{ pl: 2, mb: 1, p: 1, border: '1px solid #ddd', borderRadius: 1 }}>
                        <Typography><strong>Price ID:</strong> {price.id}</Typography>
                        <Typography><strong>Amount:</strong> ${price.unit_amount} {price.currency.toUpperCase()}</Typography>
                        <Typography><strong>Interval:</strong> {price.recurring?.interval}</Typography>
                        <Typography><strong>Active:</strong> {price.active ? 'Yes' : 'No'}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
                
                {stripeDetails.stripeError && (
                  <Alert severity="error">
                    {stripeDetails.stripeError}
                  </Alert>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowStripeDetailsDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Add/Edit Plan Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{isEditing ? 'Edit Plan' : 'Add New Plan'}</DialogTitle>
          <form onSubmit={handleSubmit}>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Plan Name"
                    value={currentPlan.name}
                    onChange={handleChange('name')}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description (HTML supported)"
                    value={currentPlan.description}
                    onChange={handleChange('description')}
                    multiline
                    rows={6}
                    required
                    helperText="HTML formatting is supported for rich descriptions"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Monthly Fee"
                    type="number"
                    value={monthlyFeeText}
                    onChange={handleMoneyChange('monthlyFee')}
                    onFocus={selectAllOnFocus}
                    inputProps={{ min: 0, step: 0.01 }}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Annual Fee"
                    type="number"
                    value={annualFeeText}
                    onChange={handleMoneyChange('annualFee')}
                    onFocus={selectAllOnFocus}
                    inputProps={{ min: 0, step: 0.01 }}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Billing Period</InputLabel>
                    <Select
                      value={currentPlan.billingPeriod}
                      label="Billing Period"
                      onChange={handleChange('billingPeriod')}
                    >
                      <MenuItem value="monthly">Monthly</MenuItem>
                      <MenuItem value="annual">Annual</MenuItem>
                      <MenuItem value="both">Both</MenuItem>
                      <MenuItem value="hybrid">Hybrid (Annual + Seats)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={currentPlan.isActive}
                        onChange={handleSwitchChange('isActive')}
                        color="primary"
                      />
                    }
                    label="Active"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={currentPlan.seatsBased}
                        onChange={handleSwitchChange('seatsBased')}
                        color="primary"
                      />
                    }
                    label="Seats Based"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={currentPlan.agentWide}
                        onChange={handleSwitchChange('agentWide')}
                        color="primary"
                      />
                    }
                    label="Agent Wide"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Seat Limit"
                    type="number"
                    value={currentPlan.seatLimit ?? ''}
                    onChange={handleChange('seatLimit')}
                    disabled={!currentPlan.seatsBased}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Seat Price"
                    type="number"
                    value={seatPriceText}
                    onChange={handleMoneyChange('seatPrice')}
                    disabled={!currentPlan.seatsBased}
                    onFocus={selectAllOnFocus}
                    inputProps={{ min: 0, step: 0.01 }}
                    helperText="Price per seat per month"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Features (comma separated)"
                    value={currentPlan.features.join(', ')}
                    onChange={handleFeaturesChange}
                    placeholder="e.g. Unlimited Workflows, Priority Support"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Storage Limit (GB)"
                    type="number"
                    value={currentPlan.storageLimit == null ? '' : currentPlan.storageLimit}
                    onChange={handleChange('storageLimit' as any)}
                    helperText="Leave blank for unlimited"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Trial Days"
                    type="number"
                    value={currentPlan.trialDays ?? 0}
                    onChange={handleChange('trialDays')}
                    onFocus={selectAllOnFocus}
                    inputProps={{ min: 0, step: 1 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Sort Order"
                    type="number"
                    value={sortOrderText}
                    onChange={handleIntegerChange('sortOrder')}
                    onFocus={selectAllOnFocus}
                    inputProps={{ min: 0, step: 1 }}
                  />
                </Grid>
                                 <Grid item xs={12}>
                   <TextField
                     fullWidth
                     label="Limits (JSON)"
                     value={currentPlan.limits ?? ''}
                     onChange={handleChange('limits')}
                     placeholder='{"workflows": 10, "storageGB": 5}'
                     multiline
                     minRows={2}
                   />
                 </Grid>
                 
                 {/* Stripe-specific fields */}
                 <Grid item xs={12}>
                   <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                     Stripe Product Settings
                   </Typography>
                 </Grid>
                 <Grid item xs={12} sm={6}>
                   <TextField
                     fullWidth
                     label="Statement Descriptor"
                     value={currentPlan.statementDescriptor ?? ''}
                     onChange={handleChange('statementDescriptor')}
                     placeholder="e.g., WORKFLOW.ESTATE"
                     helperText="Appears on customer's credit card statement"
                   />
                 </Grid>
                 <Grid item xs={12} sm={6}>
                   <TextField
                     fullWidth
                     label="Unit Label"
                     value={currentPlan.unitLabel ?? ''}
                     onChange={handleChange('unitLabel')}
                     placeholder="e.g., seat, user, license"
                     helperText="Label for the unit being sold"
                   />
                 </Grid>
                 <Grid item xs={12} sm={6}>
                   <TextField
                     fullWidth
                     label="Tax Code"
                     value={currentPlan.taxCode ?? ''}
                     onChange={handleChange('taxCode')}
                     placeholder="e.g., txcd_1234567890"
                     helperText="Stripe tax code for this product"
                   />
                 </Grid>
                 <Grid item xs={12}>
                    <TextField
                     fullWidth
                     label="Metadata (JSON)"
                      value={metadataText}
                     onChange={handleMetadataChange}
                     placeholder='{"category": "premium", "feature_flags": "advanced"}'
                     multiline
                     minRows={2}
                     helperText="Additional metadata for Stripe product"
                   />
                 </Grid>
               </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={
                  saving ||
                  currentPlan.name.trim() === '' ||
                  isNaN(Number(currentPlan.monthlyFee)) ||
                  isNaN(Number(currentPlan.annualFee))
                }
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Error Snackbar */}
        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError(null)}
        >
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Snackbar>

        {/* Success Snackbar */}
        <Snackbar
          open={!!success}
          autoHideDuration={6000}
          onClose={() => setSuccess(null)}
        >
          <Alert severity="success" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default AdminPlans; 